import { useState, useEffect, useCallback } from "react";
import { Input, Tooltip, Button } from "antd";
import {
  SearchOutlined, ReloadOutlined, EyeOutlined,
  ThunderboltOutlined, FileSearchOutlined, MailOutlined,
  TagsOutlined, HistoryOutlined, IdcardOutlined, DownloadOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { openInvoice } from "../services/invoiceService";
import SendInvoiceModal from "../invoices/SendInvoiceModal";
import Pagination from "@mui/material/Pagination";
import {
  fetchSmartSearchCustomerByUser, fetchCustomerWatches, fetchCustomerUsage,
} from "../services/smartSearchService";
import { tierTone, limitText } from "./planShared";
import {
  BRAND, INK, TONE, chip, statusChip, titleCase, num, inr, dateOnly, dateTime,
  relative, quotaOf, QuotaRing, cardStyle, StatTile, Skeleton, PanelEmpty,
  PanelError, Row, useLazyList, useDebounced, SS_CSS, paymentOf, hasTax,
} from "./customerShared";

/**
 * A customer's Smart Search subscription, as a panel inside their profile.
 *
 * Smart Search used to be its own screen with its own customer list. It is not
 * a separate business — it is one more thing a customer has bought — so it now
 * lives beside their subscription, attorneys and features, where anyone looking
 * at that customer already is.
 *
 * Three views, each with its own endpoint, each fetched only once it is opened.
 * Watched brands and the search log are long tables that most visits never look
 * at; loading all three up front would make the one everyone does look at wait
 * for the two they do not.
 */

const PAGE_SIZE = 15;

/* The response has been seen flat and nested; read once here so no panel has to
   know which. */
const pick = (d, ...keys) => {
  for (const k of keys) {
    const v = k.split(".").reduce((o, part) => (o == null ? o : o[part]), d);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
};

// ── Overview ─────────────────────────────────────────────────────────────────
/**
 * The subscription, and what has been used against it.
 *
 * No customer card: this panel sits inside that customer's own profile, whose
 * header already carries their name, email and phone. Repeating them here
 * would make the reader check whether the two agree.
 */
const Overview = ({ data }) => {
  const sub = data?.subscription || data?.plan || data;
  const summary = data?.summary || {};

  /* The payment sits beside the subscription in some shapes and inside it in
     others; hand the reader both so it is found either way. */
  const pay = paymentOf({ ...sub, payment: data?.payment ?? sub?.payment });

  const used = pick(summary, "quota_used", "searches_used", "used") ?? pick(sub, "searches_used") ?? 0;
  const quota = pick(summary, "quota", "monthly_quota") ?? pick(sub, "monthly_quota");
  const q = quotaOf(used, quota);

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ ...cardStyle, padding: 20 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 9, marginBottom: 14, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: INK.strong }}>Subscription</span>
          {statusChip(pick(sub, "status"))}
        </div>
        <Row label="Plan">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
            {pick(sub, "plan_name") || "—"}
            {pick(sub, "tier", "plan_tier") && chip(pick(sub, "tier", "plan_tier"), tierTone(pick(sub, "tier", "plan_tier")))}
          </span>
        </Row>
        <Row label="Billing">{titleCase(pick(sub, "billing_period", "billing_mode")) || "—"}</Row>
        <Row label="Purchase type">{titleCase(pick(sub, "purchase_type")) || "—"}</Row>
        <Row label="Cycle">
          {dateOnly(pick(sub, "cycle_start"))} → {dateOnly(pick(sub, "cycle_end"))}
        </Row>
        <Row
          label="Access expires"
          tip="After this date the customer keeps their data but cannot run searches. Computed by the server, so an upgrade or a grace period moves it."
        >
          {dateOnly(pick(sub, "subscription_end_date", "access_expires_at"))}
        </Row>
        {/* Read from the payment, which is what an upgrade rewrites; the
            subscription's own snapshot is only the fallback. Three lines rather
            than one sum, because "what does the plan cost" and "what did they
            pay" are different questions. Every figure is the API's own. */}
        <Row label="Base price">{inr(pay.base)}</Row>
        {hasTax(pay.tax) && (
          <Row label={`Tax${pay.percent ? ` (${pay.percent}%)` : ""}`}>{inr(pay.tax)}</Row>
        )}
        <Row label={pay.charged ? "Total charged" : "Plan total"}>
          <span style={{ fontSize: 14, fontWeight: 800 }}>{inr(pay.total)}</span>
        </Row>
        {pick(sub, "reminder_stage") !== undefined && (
          <Row
            label="Reminder stage"
            tip="How far along the renewal reminder sequence this subscription is."
          >
            {chip(titleCase(pick(sub, "reminder_stage")) || "None", TONE.amber)}
          </Row>
        )}
        {/* Payment-gateway references. Shown because they are the first thing
            asked for when a payment has to be traced, and monospaced because
            they get copied into another system rather than read. */}
        {(pick(sub, "provider_subscription_id") || pick(sub, "provider_payment_id")
          || pick(sub, "provider_order_id")) && (
          <Row label="Provider refs">
            <span style={{ fontFamily: "monospace", fontSize: 11, color: INK.body }}>
              {[
                pick(sub, "provider_subscription_id"),
                pick(sub, "provider_order_id"),
                pick(sub, "provider_payment_id"),
              ].filter(Boolean).join(" · ")}
            </span>
          </Row>
        )}
      </div>

      <div style={{ ...cardStyle, padding: 20 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: INK.strong, marginBottom: 16 }}>
          This cycle
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 26, flexWrap: "wrap" }}>
          <QuotaRing used={used} quota={quota} />
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", flex: 1, minWidth: 260 }}>
            <StatTile
              icon={<ThunderboltOutlined />} label="Searches used"
              value={num(q.used)}
              /* The server's own remaining figure when it sends one — it may
                 account for carry-over or a mid-cycle upgrade that subtracting
                 here would miss. */
              sub={q.unlimited
                ? "No monthly ceiling"
                : `${num(pick(summary, "quota_remaining") ?? q.remaining)} remaining of ${limitText(quota)}`}
            />
            <StatTile
              icon={<EyeOutlined />} label="Watches active"
              value={num(pick(summary, "watches_active", "active_watches") ?? 0)}
              color={TONE.teal.color}
            />
            <StatTile
              icon={<FileSearchOutlined />} label="Searches all-time"
              value={num(pick(summary, "searches_all_time", "total_searches") ?? 0)}
              color={TONE.indigo.color}
            />
            <StatTile
              icon={<TagsOutlined />} label="Results all-time"
              value={num(pick(summary, "results_all_time", "total_results") ?? 0)}
              color={TONE.amber.color}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Every purchase this account has made, newest first.
 *
 * An upgrade does not overwrite what came before it — it adds a row — so this
 * is where the older subscriptions live once the card at the top has moved on
 * to the current one. Read straight from `subscriptions_history`, no paging:
 * a customer has a handful of these, not a log.
 */
const PaymentHistory = ({ rows, userId, customerEmail }) => {
  const [opening, setOpening] = useState(null);
  const [sending, setSending] = useState(null);

  const th = {
    textAlign: "left", padding: "11px 14px", fontSize: 10.5, fontWeight: 700,
    color: "#8b95a8", textTransform: "uppercase", letterSpacing: "0.5px",
    background: "#fafbff", borderBottom: "1px solid #eef0f6", whiteSpace: "nowrap",
  };
  const td = { padding: "12px 14px", borderBottom: "1px solid #f6f8fc", fontSize: 12.5 };

  const download = async (r) => {
    setOpening(r.id ?? r.invoice_no);
    try {
      await openInvoice(r.receipt_download_url);
    } catch (err) {
      toast.error(err.message || "This receipt could not be opened");
    } finally {
      setOpening(null);
    }
  };

  return (
    <div style={{ ...cardStyle, overflow: "hidden", marginTop: 16 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 9, padding: "16px 18px",
        borderBottom: "1px solid #f1f4f9",
      }}>
        <span style={{
          width: 30, height: 30, borderRadius: 9, display: "inline-flex",
          alignItems: "center", justifyContent: "center",
          background: `${BRAND}14`, color: BRAND, fontSize: 13,
        }}>
          <HistoryOutlined />
        </span>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: INK.strong }}>
            Subscription &amp; Payment History
          </div>
          <div style={{ fontSize: 11.5, color: INK.muted, marginTop: 1 }}>
            Every purchase and upgrade on this account
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: 16 }}>
          <PanelEmpty message="No purchases recorded on this account yet." />
        </div>
      ) : (
        <div className="ss-sc" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead>
              <tr>
                {["Date", "Plan", "Base", "GST %", "GST amount", "Total", "Status", "Invoice", ""]
                  .map((h, i) => (
                    <th key={i} style={{
                      ...th,
                      textAlign: ["Base", "GST %", "GST amount", "Total"].includes(h) ? "right" : "left",
                    }}>
                      {h}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id ?? i} className="ss-row">
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <div style={{ color: INK.strong, fontWeight: 600 }}>{dateOnly(r.created_at)}</div>
                    <div style={{ fontSize: 11, color: INK.muted, marginTop: 2 }}>
                      {relative(r.created_at)}
                    </div>
                  </td>
                  <td style={{ ...td, color: INK.strong, fontWeight: 600 }}>{r.plan_ref || "—"}</td>
                  <td style={{ ...td, textAlign: "right", color: INK.body, fontVariantNumeric: "tabular-nums" }}>
                    {inr(r.base_inr)}
                  </td>
                  <td style={{ ...td, textAlign: "right", color: INK.body, fontVariantNumeric: "tabular-nums" }}>
                    {r.tax_percent ? `${r.tax_percent}%` : "—"}
                  </td>
                  <td style={{ ...td, textAlign: "right", color: INK.body, fontVariantNumeric: "tabular-nums" }}>
                    {hasTax(r.tax_inr) ? inr(r.tax_inr) : "—"}
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 800, color: INK.strong, fontVariantNumeric: "tabular-nums" }}>
                    {inr(r.amount_inr)}
                  </td>
                  <td style={td}>{statusChip(r.status)}</td>
                  <td style={{ ...td, fontFamily: "monospace", fontSize: 11.5, color: INK.body }}>
                    {r.invoice_no || "—"}
                  </td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                    {/* Only offered where the server gave a link — a button that
                        opens nothing is worse than no button. */}
                    {r.receipt_download_url && (
                      <Tooltip title="Open the receipt">
                        <Button
                          size="small" icon={<DownloadOutlined spin={opening === (r.id ?? r.invoice_no)} />}
                          onClick={() => download(r)}
                          style={{ marginRight: 6, borderRadius: 8 }}
                        />
                      </Tooltip>
                    )}
                    {r.receipt_send_endpoint && (
                      <Tooltip title="Email this receipt">
                        <Button
                          size="small" icon={<MailOutlined />}
                          onClick={() => setSending({
                            invoice_number: r.invoice_no || `Payment #${r.id}`,
                            title: r.plan_ref || "Smart Search",
                            amount: r.amount_inr,
                            send_endpoint: r.receipt_send_endpoint,
                          })}
                          style={{ borderRadius: 8 }}
                        />
                      </Tooltip>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SendInvoiceModal
        open={Boolean(sending)}
        invoice={sending}
        userId={userId}
        defaultEmail={customerEmail}
        onClose={() => setSending(null)}
      />
    </div>
  );
};

// ── Watched brands ───────────────────────────────────────────────────────────
const SENSITIVITY_TONE = { high: TONE.red, medium: TONE.amber, low: TONE.teal };

const WatchCard = ({ w }) => {
  const classes = Array.isArray(w.classes) ? w.classes : [];
  return (
    <div style={{ ...cardStyle, padding: 16, display: "flex", gap: 13 }}>
      {/* The logo is the customer's own upload and may 404; the tile stays
          either way so the row does not change shape when one is missing. */}
      <div style={{
        width: 46, height: 46, borderRadius: 11, flexShrink: 0, overflow: "hidden",
        background: "#f4f6fa", display: "flex", alignItems: "center",
        justifyContent: "center", color: INK.faint, fontSize: 15, fontWeight: 700,
      }}>
        {w.logo_url
          ? (
            <img
              src={w.logo_url} alt=""
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )
          : String(w.brand_name || "?").charAt(0).toUpperCase()}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: INK.strong }}>
            {w.brand_name || "—"}
          </span>
          {w.status && statusChip(w.status)}
          {w.sensitivity && chip(
            w.sensitivity,
            SENSITIVITY_TONE[String(w.sensitivity).toLowerCase()] || TONE.slate,
          )}
        </div>

        {w.proprietor && (
          <div style={{ fontSize: 12, color: INK.body, marginBottom: 7 }}>{w.proprietor}</div>
        )}

        {classes.length > 0 && (
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 8 }}>
            {classes.map((c, i) => (
              <span key={i}>{chip(`Class ${c}`, TONE.indigo, { textTransform: "none" })}</span>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 11.5, color: INK.muted }}>
          {w.linked_trade_mark && (
            <span>TM <b style={{ color: INK.body }}>{w.linked_trade_mark}</b></span>
          )}
          <Tooltip title={dateTime(w.last_scanned_at)}>
            <span>Scanned <b style={{ color: INK.body }}>{relative(w.last_scanned_at)}</b></span>
          </Tooltip>
          {w.last_scan_candidates !== undefined && w.last_scan_candidates !== null && (
            <span>
              Candidates <b style={{ color: INK.body }}>{num(w.last_scan_candidates)}</b>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const WatchesTab = ({ id, active }) => {
  const [search, setSearch] = useState("");
  const term = useDebounced(search);

  const fetcher = useCallback(
    (page, limit) => fetchCustomerWatches(id, { page, limit, search: term }),
    [id, term],
  );
  const list = useLazyList(fetcher, { active, deps: [id, term], perPage: PAGE_SIZE });

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          prefix={<SearchOutlined style={{ color: "#c0c6d4" }} />}
          placeholder="Search a brand or proprietor"
          style={{ width: 290, borderRadius: 9 }}
        />
        {list.loaded && !list.error && chip(`${num(list.total)} watched`, TONE.teal)}
        <Tooltip title="Reload">
          <Button
            icon={<ReloadOutlined />} onClick={list.reload} loading={list.loading}
            style={{ marginLeft: "auto", borderRadius: 9 }}
          />
        </Tooltip>
      </div>

      {list.loading && !list.loaded ? <Skeleton rows={5} height={78} />
        : list.error ? <PanelError message={list.error} onRetry={list.reload} />
          : list.rows.length === 0 ? (
            <PanelEmpty message={term
              ? "No brand matches that search."
              : "This customer is not watching any brands yet."} />
          ) : (
            <>
              <div style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(330px,1fr))",
                gap: 14, opacity: list.loading ? 0.55 : 1,
              }}>
                {list.rows.map((w, i) => <WatchCard key={w.id ?? i} w={w} />)}
              </div>
              {list.pageCount > 1 && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
                  <Pagination
                    count={list.pageCount} page={list.page}
                    onChange={(_, v) => list.setPage(v)} color="primary"
                  />
                </div>
              )}
            </>
          )}
    </div>
  );
};

// ── Usage ────────────────────────────────────────────────────────────────────
const UsageTab = ({ id, active }) => {
  const fetcher = useCallback(
    (page, limit) => fetchCustomerUsage(id, { page, limit }),
    [id],
  );
  const list = useLazyList(fetcher, { active, deps: [id], perPage: PAGE_SIZE });

  const th = {
    textAlign: "left", padding: "11px 16px", fontSize: 10.5, fontWeight: 700,
    color: "#8b95a8", textTransform: "uppercase", letterSpacing: "0.5px",
    background: "#fafbff", borderBottom: "1px solid #eef0f6", whiteSpace: "nowrap",
  };
  const td = { padding: "12px 16px", borderBottom: "1px solid #f6f8fc", fontSize: 12.5 };

  if (list.loading && !list.loaded) return <Skeleton rows={8} />;
  if (list.error) return <PanelError message={list.error} onRetry={list.reload} />;
  if (list.rows.length === 0) return <PanelEmpty message="No searches run on this account yet." />;

  return (
    <div style={{ ...cardStyle, overflow: "hidden", opacity: list.loading ? 0.55 : 1 }}>
      <div className="ss-sc" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
          <thead>
            <tr>
              <th style={th}>When</th>
              <th style={{ ...th, textAlign: "right" }}>Results</th>
              <th style={th}>Counted against quota</th>
            </tr>
          </thead>
          <tbody>
            {list.rows.map((r, i) => {
              /* A search that did not count is the interesting row — a failed
                 run, a cached repeat — so it is marked rather than left blank. */
              const counted = r.counted_against_quota === true
                || r.counted_against_quota === 1
                || r.counted_against_quota === "1";
              return (
                <tr key={r.id ?? i} className="ss-row">
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <div style={{ color: INK.strong, fontWeight: 600 }}>
                      {dateTime(r.created_at || r.date || r.searched_at)}
                    </div>
                    <div style={{ fontSize: 11, color: INK.muted, marginTop: 2 }}>
                      {relative(r.created_at || r.date || r.searched_at)}
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, color: INK.strong, fontVariantNumeric: "tabular-nums" }}>
                    {num(r.results_returned)}
                  </td>
                  <td style={td}>{chip(counted ? "Yes" : "No", counted ? TONE.green : TONE.slate)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {list.pageCount > 1 && (
        <div style={{
          padding: "16px 24px", borderTop: "1px solid #f0f0f0",
          display: "flex", justifyContent: "center", background: "#fafbff",
        }}>
          <Pagination
            count={list.pageCount} page={list.page}
            onChange={(_, v) => list.setPage(v)} color="primary"
          />
        </div>
      )}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const VIEWS = [
  { key: "overview", label: "Overview", icon: <IdcardOutlined /> },
  { key: "watches", label: "Watched Brands", icon: <EyeOutlined /> },
  { key: "usage", label: "Usage", icon: <HistoryOutlined /> },
];

const NotSubscribed = () => (
  <div style={{ ...cardStyle, padding: "52px 24px", textAlign: "center" }}>
    <div style={{
      width: 54, height: 54, borderRadius: 16, margin: "0 auto 16px",
      background: "#f4f3ff", color: BRAND, fontSize: 22,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <ThunderboltOutlined />
    </div>
    <div style={{ fontSize: 15, fontWeight: 700, color: INK.strong }}>
      No Smart Search subscription
    </div>
    <div style={{
      fontSize: 13, color: INK.muted, marginTop: 7, maxWidth: 420,
      marginInline: "auto", lineHeight: 1.6,
    }}>
      This customer has not bought a Smart Search plan, so there is no quota,
      no watched brands and no search history to show.
    </div>
  </div>
);

/**
 * @param {number|string} userId  the customer, as the profile knows them
 * @param {string} customerEmail  prefills the Send Receipt modal
 * @param {boolean} active        nothing is fetched until the tab is opened
 */
export default function SmartSearchPanel({ userId, customerEmail = "", active = true }) {
  const [view, setView] = useState(VIEWS[0].key);
  const [seen, setSeen] = useState({ overview: true });
  const [state, setState] = useState({ id: null, data: null, loading: true, error: "", none: false });

  const openView = (key) => {
    setView(key);
    setSeen((p) => (p[key] ? p : { ...p, [key]: true }));
  };

  /**
   * Load this customer's subscription.
   *
   * One call: the endpoint takes the user id the profile already has. Watches
   * and usage still key off the SUBSCRIPTION id, which is read out of this
   * response rather than asked for separately.
   *
   * A 404 means no Smart Search plan, which is a normal state for most
   * customers — shown as such, not as a failure.
   */
  const load = useCallback(async () => {
    setState({ id: null, data: null, loading: true, error: "", none: false });
    try {
      const data = await fetchSmartSearchCustomerByUser(userId);
      const id = pick(data, "subscription.id", "id", "subscription_id");
      setState({ id, data, loading: false, error: "", none: false });
    } catch (err) {
      if (err.status === 404) {
        setState({ id: null, data: null, loading: false, error: "", none: true });
        return;
      }
      setState({ id: null, data: null, loading: false, error: err.message, none: false });
    }
  }, [userId]);

  useEffect(() => {
    if (!active) return;
    load();
  }, [active, load]);

  if (!active) return null;
  if (state.loading) return <><style>{SS_CSS}</style><Skeleton rows={6} height={64} /></>;
  if (state.error) return <><style>{SS_CSS}</style><PanelError message={state.error} onRetry={load} /></>;
  if (state.none) return <><style>{SS_CSS}</style><NotSubscribed /></>;

  return (
    <div>
      <style>{SS_CSS}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{
          display: "inline-flex", gap: 4, background: "#fff", padding: 4,
          borderRadius: 12, border: "1px solid #eceef5",
          boxShadow: "0 1px 3px rgba(15,23,42,0.03)", flexWrap: "wrap",
        }}>
          {VIEWS.map((t) => (
            <button
              key={t.key}
              onClick={() => openView(t.key)}
              aria-pressed={view === t.key}
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                border: "none", cursor: "pointer", borderRadius: 9,
                padding: "8px 16px", fontSize: 12.5, fontWeight: 600,
                background: view === t.key ? "#eef2ff" : "transparent",
                color: view === t.key ? BRAND : INK.body,
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
        <Tooltip title="Reload">
          <Button icon={<ReloadOutlined />} onClick={load} style={{ marginLeft: "auto", borderRadius: 9 }} />
        </Tooltip>
      </div>

      {view === "overview" && (
        <>
          <Overview data={state.data} />
          <PaymentHistory
            rows={Array.isArray(state.data?.subscriptions_history)
              ? state.data.subscriptions_history
              : []}
            userId={userId}
            customerEmail={customerEmail}
          />
        </>
      )}

      {/* `active` is what triggers the fetch, so a view that has never been
          opened costs nothing. Once opened it stays mounted and hidden, so
          switching back shows what was already loaded instead of asking
          again. */}
      {(seen.watches || view === "watches") && (
        <div style={{ display: view === "watches" ? "block" : "none" }}>
          <WatchesTab id={state.id} active={view === "watches"} />
        </div>
      )}
      {(seen.usage || view === "usage") && (
        <div style={{ display: view === "usage" ? "block" : "none" }}>
          <UsageTab id={state.id} active={view === "usage"} />
        </div>
      )}
    </div>
  );
}
